import React, { useState, useEffect, useCallback } from "react";
import {
  AppUiProvider,
  Button,
  FormField,
  LoadingIndicator,
  Rows,
  Select,
  Text,
  Title,
} from "@canva/app-ui-kit";
import { auth } from "@canva/user";
import type { data as canvaData } from "@canva/intents";
import { api, type FinancialModel } from "../api/client";

export type SourceType =
  | "monthly_metrics"
  | "income_statement"
  | "cash_flow"
  | "revenue_breakdown"
  | "expense_breakdown"
  | "headcount_breakdown";

const SOURCE_OPTIONS: { value: SourceType; label: string; helper: string }[] = [
  {
    value: "monthly_metrics",
    label: "Monthly Metrics (time series)",
    helper: "Revenue, expenses, MRR, ARR, burn, cash, headcount per month — best for line/bar/area.",
  },
  {
    value: "income_statement",
    label: "Income Statement by Month",
    helper: "Revenue, COGS, Gross Profit, Operating Expenses, Net Income — best for stacked bar / multi-line.",
  },
  {
    value: "cash_flow",
    label: "Cash Flow by Month",
    helper: "Beginning/Ending Cash plus Operating, Investing, Financing — best for bar / multi-line.",
  },
  {
    value: "revenue_breakdown",
    label: "Revenue Breakdown (single month)",
    helper: "One row per revenue line for the chosen month — best for pie/donut/bar.",
  },
  {
    value: "expense_breakdown",
    label: "Expense Breakdown (single month)",
    helper: "One row per expense category for the chosen month — best for pie/donut/bar.",
  },
  {
    value: "headcount_breakdown",
    label: "Headcount by Department (single month)",
    helper: "One row per department (G&A / M&S / R&D) — best for pie/donut/bar.",
  },
];

export const TIME_SERIES_SOURCES: SourceType[] = [
  "monthly_metrics",
  "income_statement",
  "cash_flow",
];

function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function getMonthOptions() {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = -24; i <= 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    options.push({
      value,
      label: d.toLocaleDateString("en-US", { month: "short", year: "numeric" }),
    });
  }
  return options;
}

/**
 * Selection UI for the Data Connector intent.
 *
 * Pick a scenario → serialize { orgId, scenarioId, months } into DataSourceRef.source
 * and call updateDataRef so Canva previews it before committing the import.
 */
interface Props {
  request: canvaData.RenderSelectionUiRequest;
}

export function SelectionUi({ request }: Props) {
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [models, setModels] = useState<FinancialModel[]>([]);
  const [selectedModelId, setSelectedModelId] = useState<string>("");
  const [selectedScenarioId, setSelectedScenarioId] = useState<string>("");
  const [months, setMonths] = useState<number>(12);
  const [sourceType, setSourceType] = useState<SourceType>("monthly_metrics");
  const [breakdownMonth, setBreakdownMonth] = useState<string>(getCurrentMonth());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // On mount: Canva JWT → pick first linked org → token → fetch scenarios.
  // Keep it simpler than the panel's multi-org flow; the data connector is
  // scoped to a single selection at a time, so the user can always back out
  // and re-enter if they need a different org.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const canvaToken = await auth.getCanvaUserToken();
        if (cancelled) return;

        const exchange = await api.exchange(canvaToken);
        if (cancelled) return;
        if (exchange.status !== "linked" || exchange.orgs.length === 0) {
          setError("No Profitual organization linked for this Canva user. Open the Profitual app in Canva to link one first.");
          setLoading(false);
          return;
        }

        const pickedOrg = exchange.orgs[0];
        const session = await api.selectOrg(canvaToken, pickedOrg.orgId);
        if (cancelled) return;
        api.setToken(session.token);
        setOrgId(session.orgId);

        const { models } = await api.getScenarios();
        if (cancelled) return;
        setModels(models);
        if (models.length > 0) {
          setSelectedModelId(models[0].id);
          if (models[0].scenarios.length > 0) {
            setSelectedScenarioId(models[0].scenarios[0].id);
          }
        }
        setAuthReady(true);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load scenarios");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedModel = models.find((m) => m.id === selectedModelId);
  const selectedScenario = selectedModel?.scenarios.find(
    (s) => s.id === selectedScenarioId
  );

  const onConnect = useCallback(async () => {
    if (!orgId || !selectedScenarioId || !selectedScenario) return;
    setSubmitting(true);
    setError(null);
    try {
      const sourcePayload = {
        orgId,
        scenarioId: selectedScenarioId,
        source: sourceType,
        months,
        month: breakdownMonth,
      };
      const sourceLabel = SOURCE_OPTIONS.find((o) => o.value === sourceType)?.label ?? "Data";
      const ref = {
        source: JSON.stringify(sourcePayload),
        title: `${selectedScenario.name} — ${sourceLabel}`,
      };
      const res = await request.updateDataRef(ref);
      if (res.status !== "completed") {
        setError(`Canva rejected the selection (${res.status})`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to connect data");
    } finally {
      setSubmitting(false);
    }
  }, [orgId, selectedScenarioId, selectedScenario, sourceType, months, breakdownMonth, request]);

  if (loading) {
    return (
      <div style={{ padding: 16 }}>
        <Rows spacing="2u">
          <Text>Loading Profitual data sources...</Text>
          <LoadingIndicator />
        </Rows>
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <Rows spacing="1.5u">
        <Title size="xsmall">Profitual data</Title>
        <FormField
          label="Data source"
          value={sourceType}
          control={(props) => (
            <Select<SourceType>
              {...props}
              options={SOURCE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              onChange={(v) => setSourceType(v)}
              stretch
            />
          )}
        />
        <Text size="small" tone="tertiary">
          {SOURCE_OPTIONS.find((o) => o.value === sourceType)?.helper}
        </Text>

        {models.length === 0 && authReady ? (
          <Text tone="critical">
            No financial models found for this organization.
          </Text>
        ) : (
          <>
            <FormField
              label="Financial model"
              value={selectedModelId}
              control={(props) => (
                <Select
                  {...props}
                  options={models.map((m) => ({ value: m.id, label: m.name }))}
                  onChange={(v) => {
                    setSelectedModelId(v);
                    const m = models.find((x) => x.id === v);
                    setSelectedScenarioId(m?.scenarios[0]?.id ?? "");
                  }}
                  stretch
                />
              )}
            />
            {selectedModel && (
              <FormField
                label="Scenario"
                value={selectedScenarioId}
                control={(props) => (
                  <Select
                    {...props}
                    options={selectedModel.scenarios.map((s) => ({
                      value: s.id,
                      label: s.name,
                    }))}
                    onChange={setSelectedScenarioId}
                    stretch
                  />
                )}
              />
            )}
            {TIME_SERIES_SOURCES.includes(sourceType) ? (
              <FormField
                label="Months of history"
                value={String(months)}
                control={(props) => (
                  <Select
                    {...props}
                    options={[
                      { value: "6", label: "6 months" },
                      { value: "12", label: "12 months" },
                      { value: "24", label: "24 months" },
                    ]}
                    onChange={(v) => setMonths(Number(v))}
                    stretch
                  />
                )}
              />
            ) : (
              <FormField
                label="Month"
                value={breakdownMonth}
                control={(props) => (
                  <Select
                    {...props}
                    options={getMonthOptions()}
                    onChange={setBreakdownMonth}
                    stretch
                  />
                )}
              />
            )}
          </>
        )}

        {error && (
          <Text size="small" tone="critical">
            {error}
          </Text>
        )}

        <Button
          variant="primary"
          onClick={onConnect}
          loading={submitting}
          disabled={!selectedScenarioId || submitting}
          stretch
        >
          Connect this data
        </Button>
      </Rows>
    </div>
  );
}

export function SelectionRoot(props: Props) {
  return (
    <AppUiProvider>
      <SelectionUi {...props} />
    </AppUiProvider>
  );
}
